<template>
    <div class="b_article_page">
        <div class="container">
            <div class="article_page flex">
                <div v-if="$device.isDesktop" class="sidebar_part">
                    <div class="sidebar_block">
                        <navigation-menu
                            v-if="hasNavigation"
                            :navigation-items="article.navigationItems"
                            :class="[excludeFromPrintClassName]"
                        />

                        <similar-topics-side-section
                            v-else-if="hasSimilarTopics"
                            :title="article.similarArticlesTitle"
                            :articles="[
                                ...article.similarArticles,
                                ...customSimilarArticles
                            ]"
                            :categories="article.similarArticleCategories"
                            :forums="article.similarForums"
                            :events="article.similarEvents"
                            :drugs="article.similarMedicationCategories"
                            :clinical-researches="
                                article.similarClinicalResearchCategories
                            "
                            :pages="article.similarContents"
                            :class="[excludeFromPrintClassName]"
                        />
                    </div>
                </div>

                <div class="main_part">
                    <div class="main_part_head">
                        <div class="decoration">
                            <picture
                                v-full-image-path:assets="
                                    'decor_event_article.svg'
                                "
                            />
                        </div>

                        <back-button
                            v-if="!$device.isDesktop"
                            :items="breadcrumbs"
                        />

                        <div v-if="isClinicalResearch" class="head_block">
                            <breadcrumbs
                                v-if="$device.isDesktop"
                                :items="breadcrumbs"
                                :class="[excludeFromPrintClassName]"
                            />

                            <div class="page_title">
                                <h1>{{ article.name }}</h1>
                            </div>

                            <div class="head_block_info flex">
                                <div class="info_right_part" />

                                <div class="info_left_part">
                                    <div v-if="showDates" class="dates_wrapper">
                                        <div
                                            v-if="article.publishDate"
                                            class="date"
                                        >
                                            <span>
                                                {{
                                                    $t(
                                                        'articles.head.publishedAt'
                                                    )
                                                }}
                                            </span>
                                            <span>{{ publishedAt }}</span>
                                        </div>
                                        <div
                                            v-if="article.updateDate"
                                            class="date"
                                        >
                                            <span>
                                                {{
                                                    $t(
                                                        'articles.head.updatedAt'
                                                    )
                                                }}
                                            </span>
                                            <span>{{ updatedAt }}</span>
                                        </div>
                                    </div>
                                    <div
                                        class="buttons_control flex"
                                        :class="[excludeFromPrintClassName]"
                                    >
                                        <share-button :item="article" />

                                        <div class="button_row">
                                            <button
                                                class="btn_reset btn_printing"
                                                type="button"
                                                @click="printArticle"
                                            >
                                                {{ $t('general.print') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <split-text
                                v-if="splitTextSection"
                                :value="splitTextSection"
                                :article="article"
                            />
                        </div>

                        <template v-else>
                            <div class="head_block">
                                <breadcrumbs
                                    v-if="$device.isDesktop"
                                    :items="breadcrumbs"
                                    :class="[excludeFromPrintClassName]"
                                />

                                <div class="page_title">
                                    <h1>{{ article.name }}</h1>
                                </div>

                                <div class="head_block_info flex">
                                    <div class="info_right_part">
                                        <div class="categories_wrapper flex">
                                            <div class="categories_list flex">
                                                <div
                                                    v-for="(
                                                        category, index
                                                    ) in article.categories"
                                                    :key="category.id"
                                                    class="category_tag"
                                                    :style="{
                                                        'background-color':
                                                            categoryColors[
                                                                index % 3
                                                            ]
                                                    }"
                                                >
                                                    {{ category.name }}
                                                </div>
                                                <nuxt-link
                                                    v-if="
                                                        isBranchActivityVisible
                                                    "
                                                    :to="branchLink"
                                                    class="category_tag category_seminar"
                                                >
                                                    {{ branch.title }}
                                                </nuxt-link>
                                            </div>
                                        </div>

                                        <div class="short_info">
                                            <p>
                                                {{ article.note }}
                                            </p>
                                        </div>
                                    </div>

                                    <div class="info_left_part">
                                        <div
                                            v-if="showDates"
                                            class="dates_wrapper"
                                        >
                                            <div
                                                v-if="article.publishDate"
                                                class="date"
                                            >
                                                <span>
                                                    {{
                                                        $t(
                                                            'articles.head.publishedAt'
                                                        )
                                                    }}
                                                </span>
                                                <span>{{ publishedAt }}</span>
                                            </div>
                                            <div
                                                v-if="article.updateDate"
                                                class="date"
                                            >
                                                <span>
                                                    {{
                                                        $t(
                                                            'articles.head.updatedAt'
                                                        )
                                                    }}
                                                </span>
                                                <span>{{ updatedAt }}</span>
                                            </div>
                                        </div>
                                        <div
                                            class="buttons_control flex"
                                            :class="[excludeFromPrintClassName]"
                                        >
                                            <share-button :item="article" />

                                            <div class="button_row">
                                                <button
                                                    class="btn_reset btn_printing"
                                                    type="button"
                                                    @click="printArticle"
                                                >
                                                    {{ $t('general.print') }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <div
                            v-if="!$device.isDesktop && hasNavigation"
                            class="mobile_nav_article"
                        >
                            <div class="nav_article">
                                <button
                                    class="btn_show_nav jsToggleMobileSidebar"
                                    :class="{
                                        active: showMobileMenu
                                    }"
                                    type="button"
                                    @click="showMobileMenu = !showMobileMenu"
                                >
                                    {{ $t('articles.toc') }}
                                </button>

                                <navigation-menu
                                    v-if="showMobileMenu"
                                    :navigation-items="article.navigationItems"
                                    :class="[excludeFromPrintClassName]"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="main_part_body">
                        <div class="content">
                            <content-builder
                                :article="article"
                                :exclude-from-print-class-name="
                                    excludeFromPrintClassName
                                "
                            />

                            <div
                                v-if="areMoreBranchActivityArticles"
                                class="flex justify_center more_branch_activities_section"
                            >
                                <nuxt-link
                                    :to="branchLink"
                                    class="btn btn_blue_empty"
                                >
                                    {{
                                        $t('articles.showMoreBranchActivities')
                                    }}
                                </nuxt-link>
                            </div>
                        </div>

                        <similar-topics-bottom-section
                            v-if="showSimilarTopicsBottomSection"
                            :title="article.similarArticlesTitle"
                            :articles="[
                                ...article.similarArticles,
                                ...customSimilarArticles
                            ]"
                            :categories="article.similarArticleCategories"
                            :forums="article.similarForums"
                            :events="article.similarEvents"
                            :drugs="article.similarMedicationCategories"
                            :clinical-researches="
                                article.similarClinicalResearchCategories
                            "
                            :pages="article.similarContents"
                            :class="[excludeFromPrintClassName]"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ArticleType } from '@/enums/articles';
import headMixin from '@/mixins/head.mixin.js';
import BackButton from '@/components/elements/BackButton';
import Breadcrumbs from '@/components/elements/Breadcrumbs';
import ShareButton from '@/components/articles/ShareButton';
import ContentBuilder from '@/components/elements/ContentBuilder';
import NavigationMenu from '@/components/articles/NavigationMenu';
import SplitText from '@/components/elements/ContentBuilder/sections/SplitText';
import SimilarTopicsSideSection from '@/components/articles/SimilarTopicsSideSection';
import SimilarTopicsBottomSection from '@/components/articles/SimilarTopicsBottomSection';

export default {
    components: {
        SplitText,
        BackButton,
        Breadcrumbs,
        ShareButton,
        ContentBuilder,
        NavigationMenu,
        SimilarTopicsSideSection,
        SimilarTopicsBottomSection
    },

    mixins: [headMixin],

    props: {
        article: {
            type: Object,
            required: true
        },

        breadcrumbs: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            excludeFromPrintClassName: 'hide-from-print',
            categoryColors: ['#C9DEB3', '#E9BBF4', 'rgba(241, 91, 98, 0.4)'],
            showMobileMenu: false,
            meta: this.article.meta
        };
    },

    computed: {
        headTitle() {
            return this.article.name;
        },

        showSimilarTopicsBottomSection() {
            if (!this.hasSimilarTopics) {
                return false;
            }

            return [
                ArticleType.EVENT,
                ArticleType.MEDICATION,
                ArticleType.BRANCH_ACTIVITIES,
                ArticleType.SIMPLE_ARTICLE,
                ArticleType.DRUG
            ].includes(this.article.type);
        },

        customSimilarArticles() {
            return Array.isArray(this.article.customSimilarArticles)
                ? this.article.customSimilarArticles
                : JSON.parse(this.article.customSimilarArticles);
        },

        isClinicalResearch() {
            return this.article.type === ArticleType.CLINICAL_RESEARCH;
        },

        hasSimilarTopics() {
            return (
                this.similarTopicsCount ||
                this.article.customSimilarArticles.length
            );
        },

        hasNavigation() {
            const { navigationItems, isLong } = this.article;

            return !!(isLong && navigationItems?.length);
        },

        showDates() {
            return this.article.publishDate || this.article.updateDate;
        },

        publishedAt() {
            return this.$dayjs(this.article.publishDate).format('D.M.YYYY');
        },

        updatedAt() {
            return this.$dayjs(this.article.updateDate).format('D.M.YYYY');
        },

        splitTextSection() {
            const section = this.article.content.find(
                section => section.key === 'split-text'
            );

            return section?.value;
        },

        similarTopicsCount() {
            const {
                similarArticles = [],
                similarArticleCategories = [],
                similarForums = [],
                similarEvents = [],
                similarMedicationCategories = [],
                similarClinicalResearchCategories = [],
                similarContents = []
            } = this.article;

            return (
                similarArticles.length +
                similarArticleCategories.length +
                similarForums.length +
                similarEvents.length +
                similarMedicationCategories.length +
                similarClinicalResearchCategories.length +
                similarContents.length
            );
        },

        isBranchActivity() {
            return this.article.type === ArticleType.BRANCH_ACTIVITIES;
        },

        branch() {
            return this.article.branch;
        },

        isBranchActivityVisible() {
            return this.isBranchActivity && this.branch;
        },

        areMoreBranchActivityArticles() {
            return (
                this.isBranchActivityVisible &&
                (this.branch?.articles?.length || 0) > 1
            );
        },

        branchLink() {
            if (!this.branch?.slug) {
                return this.localePath('/');
            }

            return this.localePath({
                path: '/branch-activities',
                query: {
                    branches: this.branch.slug
                }
            });
        }
    },

    mounted() {
        document.querySelector('base').setAttribute('href', window.location);
    },

    methods: {
        printArticle() {
            const printWindow = window.open(
                window.location.href,
                '',
                'height=1000,width=1000'
            );

            printWindow.focus();
            printWindow.print();
        },

        scrollTo(id) {
            const element = document.getElementById(id);

            window.scrollTo({
                top: element.offsetTop - 50,
                behavior: 'smooth'
            });
        }
    }
};
</script>
